/**
 * Route focus (TMMAPP-0072, A11Y-010 / TMM-B7): after a client-side navigation to a different
 * path, focus moves to the new page's `h1` (`#page-title`), else the first `h1` inside a `main`
 * (the wizard frames), else `#main`. Mounted once in `RootLayout`, so it covers every route.
 *
 * It stays out of the way of every other focus owner:
 * - the first load (the initial location and the redirects it triggers) never moves focus — it
 *   starts after the first keyboard, pointer or `click` event in this document (a screen reader's
 *   browse mode activates a link with a synthesized `click` and sends no key or pointer event);
 * - a search-param-only change (a filter, a tab: `useUrlState`) is not a path change;
 * - focus already inside a `main` after the commit is kept: an `ErrorState` title (it focuses
 *   itself on mount, before this parent effect runs), an autofocused field, or something the user
 *   focused deliberately;
 * - `preventScroll`, so `ScrollRestoration` (it scrolls in a layout effect, before this one) keeps
 *   a restored position on Back and a new page at the top.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function focusTarget(): HTMLElement | null {
  return (
    document.getElementById('page-title') ??
    document.querySelector<HTMLElement>('main h1') ??
    document.getElementById('main')
  );
}

function focusAfterNavigation(): void {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) {
    const inMain = active.closest('main') !== null;
    if (inMain && active.id !== 'main') return;
  }
  const target = focusTarget();
  if (target === null) return;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

export function RouteFocus() {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  const interacted = useRef(false);

  useEffect(() => {
    const mark = () => {
      interacted.current = true;
    };
    document.addEventListener('keydown', mark, true);
    document.addEventListener('pointerdown', mark, true);
    document.addEventListener('click', mark, true);
    return () => {
      document.removeEventListener('keydown', mark, true);
      document.removeEventListener('pointerdown', mark, true);
      document.removeEventListener('click', mark, true);
    };
  }, []);

  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    if (!interacted.current) return;
    focusAfterNavigation();
  }, [pathname]);

  return null;
}
